import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';

const CuetCounsellingPage = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', type: '', course: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/leads', { ...form, source: 'CUET Counselling Page' });
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    { q: 'Is CUET counselling free?', a: 'Yes, we offer free counselling for basic guidance on courses, colleges, and the admission process.' },
    { q: 'What is included in free counselling?', a: 'Basic guidance on courses, colleges, cut-offs, and admission timelines.' },
    { q: 'Do you provide admission support?', a: 'Yes, through our paid admission consultancy service with end-to-end support.' },
    { q: 'How do I book counselling?', a: 'Fill the form on this page or connect via call/WhatsApp for instant response.' },
    { q: 'Is this useful after CUET result?', a: 'Yes — counselling is most important after results to make the right college choice.' },
  ];

  return (
    <>
      <Helmet>
        <title>CUET Counselling 2026 | Free Guidance for UG & PG Admissions</title>
        <meta name="description" content="Get expert CUET counselling for UG & PG admissions. Book free counselling, choose the right college, and secure your admission with confidence." />
        <meta name="keywords" content="CUET counselling, CUET UG counselling, CUET PG counselling, CUET admission guidance, free CUET counselling" />
        <link rel="canonical" href="https://besst.in/cuet-counselling" />
        <meta property="og:title" content="CUET Counselling 2026 | Free Guidance for UG & PG Admissions" />
        <meta property="og:description" content="Get expert CUET counselling for UG & PG admissions. Book free counselling and secure your admission with confidence." />
        <meta property="og:url" content="https://besst.in/cuet-counselling" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BESST" />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20">
        {/* Hero */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="hero-badge mx-auto mb-6">🎓 Free Expert Guidance</div>
            <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-6 leading-tight">
              CUET Counselling 2026
            </h1>
            <p className="text-xl text-text-muted mb-4">
              Confused about CUET counselling, college selection, or admission process?
            </p>
            <p className="text-lg text-text-muted mb-8">
              ✅ Personalized Guidance &nbsp;•&nbsp; 🏫 University Selection &nbsp;•&nbsp; 📋 Admission Support
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#book" className="btn-primary px-8 py-4 text-lg font-bold rounded-xl">
                Book Free Counselling Now
              </a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noreferrer"
                className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
                💬 Talk to an Expert
              </a>
            </div>
            <p className="text-xs text-text-muted mt-4">⚡ Limited slots available for free counselling</p>
          </div>
        </div>

        {/* What is CUET Counselling */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-4">📊 What is CUET Counselling?</h2>
            <p className="text-text-muted mb-6">
              CUET counselling is the process through which students secure admission into top universities after qualifying CUET UG or PG exams. Proper counselling can significantly increase your chances of getting into a top university.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {['College and course selection', 'Cut-off analysis & strategy', 'Admission process guidance', 'Document verification support'].map(item => (
                <div key={item} className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl">
                  <span className="text-green-400 text-lg">✔</span>
                  <span className="text-text-light">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why You Need Counselling */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Avoid Mistakes & Make the Right Decision</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="glass-card border-red-500/20">
              <h3 className="text-lg font-bold text-red-400 mb-4">❌ Without Counselling</h3>
              <ul className="space-y-3 text-text-muted">
                {['Choose wrong colleges', 'Miss important deadlines', 'Lack clarity about courses'].map(i => (
                  <li key={i} className="flex items-center gap-2"><span className="text-red-400">✗</span>{i}</li>
                ))}
              </ul>
            </div>
            <div className="glass-card border-green-500/20">
              <h3 className="text-lg font-bold text-green-400 mb-4">✅ With Expert Counselling</h3>
              <ul className="space-y-3 text-text-muted">
                {['Choose the right university', 'Understand cut-offs & eligibility', 'Plan admission strategy', 'Avoid costly mistakes'].map(i => (
                  <li key={i} className="flex items-center gap-2"><span className="text-green-400">✔</span>{i}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Types of Counselling */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-3xl font-bold gradient-text text-center mb-10">Choose the Right Guidance for Your Needs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { color: 'green', title: '🟢 Free Counselling', badge: 'Free', items: ['Basic guidance', 'Doubt clearing', 'Course & college overview'] },
              { color: 'blue', title: '🔵 Advanced Counselling', badge: 'Paid', items: ['Personalized strategy', 'University shortlisting', 'Cut-off analysis'] },
              { color: 'purple', title: '🟣 Admission Consultancy', badge: 'Paid', items: ['Complete admission support', 'Application assistance', 'End-to-end guidance'] },
            ].map(tier => (
              <div key={tier.title} className="glass-card text-center">
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3 bg-${tier.color}-500/20 text-${tier.color}-400`}>{tier.badge}</div>
                <h3 className="text-lg font-bold text-text-light mb-4">{tier.title}</h3>
                <ul className="space-y-2 text-text-muted text-sm">
                  {tier.items.map(i => <li key={i}>• {i}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-text-muted mt-6 text-sm">👉 Start with free counselling and upgrade if needed</p>
        </div>

        {/* Lead Form */}
        <div id="book" className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-2xl mx-auto">
            {submitted ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-text-light mb-2">Request Received!</h3>
                <p className="text-text-muted">Our counsellor will contact you within 24 hours.</p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-text-light mb-2">Start Your Free CUET Counselling Today</h2>
                <p className="text-text-muted mb-6 text-sm">Fill in your details — takes less than 30 seconds</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input required placeholder="Full Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary transition-colors" />
                  <input required type="tel" placeholder="Phone Number *" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary transition-colors" />
                  <input required type="email" placeholder="Email Address *" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary transition-colors" />
                  <select required value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary transition-colors">
                    <option value="">Select CUET UG / CUET PG *</option>
                    <option>CUET UG</option>
                    <option>CUET PG</option>
                    <option>Both</option>
                  </select>
                  <input placeholder="Preferred Course (optional)" value={form.course} onChange={e => setForm({...form, course: e.target.value})}
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary transition-colors" />
                  <button type="submit" disabled={submitting} className="w-full btn-primary py-4 text-lg font-bold disabled:opacity-50">
                    {submitting ? 'Submitting...' : '🎓 Get Free Counselling'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Connect Options */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Talk to a CUET Expert Now</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            {[
              { icon: '📞', label: 'Call Counsellor', href: 'tel:+919999999999' },
              { icon: '💬', label: 'WhatsApp Support', href: 'https://wa.me/919999999999' },
              { icon: '📅', label: 'Book a Slot', href: '#book' },
            ].map(c => (
              <a key={c.label} href={c.href} className="glass-card text-center hover:border-primary/50 transition-colors">
                <div className="text-3xl mb-2">{c.icon}</div>
                <div className="text-text-light font-semibold text-sm">{c.label}</div>
              </a>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="glass-card group">
                <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">
                  {f.q} <span className="text-primary text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-text-muted mt-3 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="glass-card text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-3">Don't Risk Your Admission</h2>
            <p className="text-text-muted mb-6">Make informed decisions and secure your future with the right guidance.</p>
            <p className="text-xs text-accent-gold mb-6">⚡ Limited free counselling slots available</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#book" className="btn-primary px-8 py-3 font-bold rounded-xl">Book Free Counselling Now</a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noreferrer"
                className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">
                💬 Talk to Expert
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CuetCounsellingPage;
